import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./machines-list.module.scss";
import { useI18N } from "@src/language/language-context";
import { MachinesListSupportSessionsZitadelRoleTypes } from "@common/types/machines-list-types";
import { MachinesListMachineTypes } from "@src/types/gateways-with-machines";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { MachineListRow } from "@src/components/molecules/machines-list-row/machines-list-row";
import cn from "classnames";
import { useHandleVncMessages } from "./use-handle-vnc-message";
import { warningSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { getSessionIdentityFlags } from "@src/cloud-connect/vpn-manager/utils/session-identity-flags";

const cssClassName = "machines-list";

interface MachinesListPropsTypes {
  allMachinesByCompany: MachinesListMachineTypes[];
  selectedMachineId?: string;
}

export const MachinesList = memo(
  ({ allMachinesByCompany, selectedMachineId }: MachinesListPropsTypes) => {
    const activeOtherUserVisibleMachineIdsRef = useRef<Set<string>>(new Set());
    const [shouldEnableScroll, setShouldEnableScroll] = useState(false);

    const i18n = useI18N();

    const authContext = useSelector(
      (state: RootState) => state.yourSlice.authContext,
    );
    const organization = useSelector(
      (state: RootState) => state.yourSlice.authContext.organization,
    );

    useHandleVncMessages(); // custom hook to handle messages from VNC tabs (e.g., tab closure, connection failure with retry)

    const texts = useMemo(
      () => ({
        statusSourceName: i18n("modules.machinesList.statusSourceName"),
        sourceType: i18n("modules.machinesList.sourceType"),
        gateway: i18n("modules.machinesList.gateway"),
        machineId: i18n("modules.machinesList.machineId"),
        machineAddress: i18n("modules.machinesList.machineAddress"),
        statusGatewayName: i18n("modules.machinesList.statusGatewayName"),
        STremoteSupport: i18n("modules.machinesList.STremoteSupport"),
        OTremoteSupport: i18n("modules.machinesList.OTremoteSupport"),
        sessionEndedByTechnician: i18n(
          "modules.machinesListRow.sessionEndedByTechnician",
        ),
      }),
      [i18n],
    );

    const { showSTSupport, showOTSupport } = useMemo(() => {
      const allPermissions = authContext?.permissions ?? [];
      const hasManage = allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.MANAGE_VPN,
      );
      const hasAccept = allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.ACCEPT_VPN,
      );
      const hasRequest = allPermissions.includes(
        MachinesListSupportSessionsZitadelRoleTypes.REQUEST_VPN,
      );

      return {
        showSTSupport: hasManage || hasRequest,
        showOTSupport: hasManage || hasAccept,
      };
    }, [authContext?.permissions]);

    const machineHeader = useMemo(() => {
      const header = [
        texts.statusSourceName,
        texts.machineId,
        texts.machineAddress,
        texts.gateway,
      ];
      if (showSTSupport) header.push(texts.STremoteSupport);
      if (showOTSupport) header.push(texts.OTremoteSupport);
      return header;
    }, [showOTSupport, showSTSupport, texts]);

    // Tracks which rows currently show "Active other user" and enables table scroll
    // whenever at least one row reports that state.
    const handleActiveOtherUserVisibilityChange = useCallback(
      (machineId: string, isVisible: boolean) => {
        const visibleSet = activeOtherUserVisibleMachineIdsRef.current;

        if (isVisible) {
          visibleSet.add(machineId);
        } else {
          visibleSet.delete(machineId);
        }

        setShouldEnableScroll(visibleSet.size > 0);
      },
      [],
    );

    // TODO: this is a temporary solution to listen to VNC tab closure, might be needed elsewhere
    useEffect(() => {
      const handleVncMessage = (event: MessageEvent) => {
        if (event.origin !== window.location.origin) return;

        const { type, sessionId } = event.data || {};

        if (type !== "VNC_CLOSED" || !sessionId) return;

        const currentClosedMachine = allMachinesByCompany?.find(
          (machine) => machine.id === sessionId,
        );
        const { isCurrentUserSessionOwner } = getSessionIdentityFlags(
          currentClosedMachine,
          authContext,
        );

        if (!isCurrentUserSessionOwner) return;
        warningSnackbar(texts.sessionEndedByTechnician);

        // TODO: do we need logic to be used in the frontend?
      };

      window.addEventListener("message", handleVncMessage);

      return () => {
        window.removeEventListener("message", handleVncMessage);
      };
    }, [
      allMachinesByCompany,
      authContext,
      authContext?.userId,
      texts.sessionEndedByTechnician,
    ]);

    return (
      <div className={styles[cssClassName]}>
        <div
          className={cn(styles[`${cssClassName}__table-wrapper`], {
            [styles[
              `${cssClassName}__table-wrapper--active-other-user-scroll`
            ]]: shouldEnableScroll,
          })}
        >
          <table className={styles[`${cssClassName}__table`]}>
            <thead>
              <tr
                className={cn(styles[`${cssClassName}__machines-header`], {
                  [styles[
                    `${cssClassName}__machines-header--manage-machines-columns-display`
                  ]]: showSTSupport && showOTSupport,
                  [styles[
                    `${cssClassName}__machines-header--technician-or-operator-column-display`
                  ]]: showSTSupport !== showOTSupport,
                  [styles[
                    `${cssClassName}__machines-header--no-permission-columns-display`
                  ]]: !showSTSupport && !showOTSupport,
                })}
              >
                {machineHeader.map((label, i) => (
                  <th
                    key={i}
                    className={styles[`${cssClassName}__header-cell`]}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className={styles[`${cssClassName}__table-body`]}>
              {allMachinesByCompany?.map((machine) => (
                <MachineListRow
                  key={machine.machine_id}
                  machine={machine}
                  companyName={machine.company_name ?? organization}
                  showSTSupport={showSTSupport}
                  showOTSupport={showOTSupport}
                  onActiveOtherUserVisibilityChange={
                    handleActiveOtherUserVisibilityChange
                  }
                  selectedMachineId={selectedMachineId}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  },
);

MachinesList.displayName = "MachinesList";
